import React, { useEffect, useState } from 'react';
import { useClassName, useWindowSize } from 'common/hooks';
import './get-tickets.less';
import { DateTime } from 'luxon';
import OperationalHoursModal from 'public/screens/CustomLandingPage/Components/OperationalHoursModal/OperationalHoursModal';
import { inject } from 'mobx-react';
import {
  getContentfulAssetUrlandAlt,
  getContentfulField,
} from 'common/components';
import {
  formatOpeningHoursForDate,
  isVenueDateUnavailable,
} from 'utils/helpers/openingHours';
import {
  categories,
  getCategoryByCta,
  standardizedTrack,
} from 'utils/analytics';
import {
  parseAttraction,
  parseTicketOptions,
} from 'public/helpers/contentful/parser';
import { getContentRides } from 'public/components/AttractionScreen/RideClosures';
import useCustomStyle from 'public/screens/CustomLandingPage/hooks';
import { TicketOptionsModal } from 'public/components/AttractionScreen/TicketOptionsModal';
import { FavoriteVenue } from 'public/components/Favorite';

const GetTickets = ({ venue, openingHoursBanner, betaFeatures }) => {
  const className = useClassName('GetTickets');
  const { isMobile, isTablet, isDesktop } = useWindowSize();
  const openingHours = venue.openingHours;
  const localDate = DateTime.local();
  const [date, setDate] = useState(localDate);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [openHoursModal, setOpenHoursModal] = useState(false);
  const [isParkEntryEnabled, setIsParkEntryEnabled] = useState(false);
  const parkAccessMessage = getContentfulField(
    venue?.parkScreen?.parkAccessMessage
  );

  const getLabel = () => {
    const comingSoon = venue?.featuredFlags.includes('Coming Soon');

    if (comingSoon) return 'Coming Soon';

    return formatOpeningHoursForDate(openingHours, date);
  };

  useEffect(() => {
    if (!venue) return;
    const unavailableDates = venue?.priceTypes?.reduce(
      (acc, { date, type }) => (type === 'closed' ? acc.concat(date) : acc),
      []
    );
    setUnavailableDates(unavailableDates);
  }, [venue]);

  useEffect(() => {
    const getParkEntryEnabled = async () => {
      const isParkEntryEnabled = await betaFeatures?.fetch('Park Entry Pass');
      if (isParkEntryEnabled) {
        setIsParkEntryEnabled(isParkEntryEnabled);
      }
    };
    getParkEntryEnabled();
  }, []);

  const isDateUnavailable = (isoDate) =>
    isVenueDateUnavailable(venue, isoDate, unavailableDates);

  const getDate = () => {
    const today = DateTime.local();
    return today.toFormat('yyyy-MM-dd') === date.toFormat('yyyy-MM-dd')
      ? 'Today'
      : date.toFormat('LLL dd');
  };

  const trackingLabel = window.location.pathname; //We save this here to make sure the window location is not overwritten before sending the track events

  const handleClick = (heroCtaLink, heroCtaText) => {
    const category = getCategoryByCta(heroCtaLink) || categories.ERC_INFO;

    standardizedTrack(
      'click',
      'button',
      'hero',
      heroCtaText,
      category,
      trackingLabel
    );
  };

  const ticketOptions = venue.ticketOptions
    ? parseTicketOptions(venue.ticketOptions)
    : venue.content?.attractionScreen
    ? parseAttraction(venue.content.attractionScreen)?.ticketOptions
    : [];

  const logoFields = getContentfulField(venue.parkScreen.logo).fields;
  const logo = {
    desktop: getContentfulAssetUrlandAlt(logoFields.desktop),
    mobile: getContentfulAssetUrlandAlt(logoFields.mobile),
    name: getContentfulField(logoFields.name),
  };
  const parkName = getContentfulField(venue.parkScreen.name);
  const ridesCards = getContentRides(venue);
  const openingHoursBannerFields = getContentfulField(
    venue.parkScreen.openingHoursBanner
  )?.fields;
  const emptyAsset = getContentfulField(openingHoursBannerFields?.logoOverlay);
  const settingsField = getContentfulField(openingHoursBannerFields?.settings);

  const rawSettings =
    settingsField?.mobile || settingsField?.tablet || settingsField?.desktop
      ? (isMobile && settingsField.mobile) ||
        (isTablet && settingsField.tablet) ||
        (isDesktop && settingsField.desktop)
      : settingsField;

  const processedSettings = (Array.isArray(rawSettings) ? rawSettings : []).map(
    (setting) => ({
      ...setting,
      fields: {
        key: getContentfulField(setting.fields?.key),
        name: getContentfulField(setting.fields?.name),
        value: getContentfulField(setting.fields?.value),
        mobileValue: getContentfulField(setting.fields?.mobileValue),
        tabletValue: getContentfulField(setting.fields?.tabletValue),
        desktopValue: getContentfulField(setting.fields?.desktopValue),
      },
    })
  );

  const [, style] = useCustomStyle(processedSettings);
  const emptyAssetStyle = Object.entries(style).reduce((acc, [key, value]) => {
    if (key.startsWith('--logo-overlay-')) {
      acc[key.replace('--logo-overlay-', '')] = value;
    }
    return acc;
  }, {});

  return (
    <div className={className('container')}>
      <div className={className('logo')}>
        <img src={logo.desktop.assetUrl} alt={logo.name} />
      </div>
      <div className={className('calendar')}>
        <div className={className('calendar__info')}>
          <p className={className('calendar__title')}>{getDate()}</p>
          <p className={className('calendar__label')}>{getLabel()}</p>
        </div>
        <div className={className('calendar__modal')}>
          <OperationalHoursModal
            ridesCards={ridesCards}
            isDateUnavailable={isDateUnavailable}
            open={openHoursModal}
            openingHours={openingHours}
            trigger={
              <p className={className('calendar__link')}>
                <a onClick={() => setOpenHoursModal(true)}>
                  {parkName} Calendar
                </a>
              </p>
            }
            onOpen={() => setOpenHoursModal(true)}
            onClose={() => setOpenHoursModal(false)}
            initialDate={date.toISO()}
            updateSelectedDate={(jsDate) => {
              if (jsDate instanceof Date) {
                setDate(DateTime.fromJSDate(jsDate));
              }
            }}
            openingHoursBanner={openingHoursBanner}
            venueSlug={venue.slug}
            venueId={venue.id}
            emptyAsset={emptyAsset}
            emptyAssetStyle={emptyAssetStyle}
          />
        </div>
      </div>
      <div className={className('actions')}>
        <div className={className('cta')}>
          {/* <Link
            to={`/venue/${venue.slug}/tickets`}
            className={className('cta__button')}>
            Get Tickets
          </Link> */}

          <TicketOptionsModal
            trigger={
              <div className={className('cta__button')}>Get Tickets</div>
            }
            parkAccessMessage={isParkEntryEnabled ? parkAccessMessage : null}
            options={ticketOptions}
            onClick={handleClick}
          />
        </div>
        <FavoriteVenue
          size="medium"
          venueId={venue.id}
          className={className('favorite')}
        />
      </div>
    </div>
  );
};

export default inject('betaFeatures')(GetTickets);
